/* ============================================
   Avalna PDP Recommendations — Batch 05c, price cache fixed in Batch 31
   Recently Viewed (localStorage identity + live price) + You May Also Like (Recs API)
   ============================================ */

(function() {
  'use strict';

  /* Batch 31: key bumped from 'avalna-recently-viewed' to '-v2'.
     The v1 payload persisted `price` and `compareAtPrice`, so a returning visitor whose
     browser cached a product before the store-wide compare-at clear kept seeing a phantom
     discount pill on a product now selling at full price. Bumping the key orphans every
     v1 entry on every device at once — no per-entry validation, no cache-busting.
     v2 persists identity and display fields only; prices are fetched live at render time
     (avalnaHydrateRecentlyViewedPrices) so this bug class cannot return the next time any
     price changes. */
  var STORAGE_KEY = 'avalna-recently-viewed-v2';
  var STORAGE_KEY_V1 = 'avalna-recently-viewed';
  var MAX_STORED = 8;
  var RECS_LIMIT = 6;

  // --- Utility: escape for HTML text or a quoted attribute ---
  function avalnaEsc(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/"/g, '&quot;')
      .replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // --- Utility: format price from cents to ₹X,XXX ---
  function avalnaFormatPrice(cents) {
    if (!cents) return '';
    var rupees = Math.round(cents / 100);
    return '\u20b9' + rupees.toLocaleString('en-IN');
  }

  // --- Utility: calculate discount percentage ---
  function avalnaCalcDiscount(price, compareAtPrice) {
    if (!compareAtPrice || compareAtPrice <= price) return 0;
    return Math.round((1 - price / compareAtPrice) * 100);
  }

  // --- Utility: build the price markup (shared by both rows) ---
  function avalnaBuildPriceHtml(price, compareAtPrice) {
    var html = '<span class="avalna-recommendations__price">' + avalnaFormatPrice(price) + '</span>';
    var discount = avalnaCalcDiscount(price, compareAtPrice);
    if (discount > 0) {
      html += '<span class="avalna-recommendations__compare-price">' + avalnaFormatPrice(compareAtPrice) + '</span>';
      html += '<span class="avalna-recommendations__discount-badge">' + discount + '% off</span>';
    }
    return html;
  }

  /* Utility: build a product card HTML string.
     pricePending = true renders a reserved, empty price line for a Recently Viewed card
     whose real price is still in flight. The placeholder is a real price span holding a
     non-breaking space, so it occupies exactly the same line box as the finished price
     (measured 23.39px on live) and hydration causes no layout shift — with no magic
     number in the CSS to drift if the type scale ever changes. */
  function avalnaBuildCard(product, pricePending) {
    var priceHtml = pricePending
      ? '<span class="avalna-recommendations__price">&nbsp;</span>'
      : avalnaBuildPriceHtml(product.price, product.compareAtPrice);

    var safeTitle = avalnaEsc(product.title);

    return '<a href="' + avalnaEsc(product.url) + '" class="avalna-recommendations__card" role="listitem">' +
      '<img class="avalna-recommendations__card-image" src="' + avalnaEsc(product.image) + '" alt="' + safeTitle + '" loading="lazy" width="160" height="160">' +
      '<p class="avalna-recommendations__card-title">' + safeTitle + '</p>' +
      '<div class="avalna-recommendations__card-prices">' + priceHtml + '</div>' +
      '</a>';
  }

  // --- Utility: build peek card HTML ---
  function avalnaBuildPeek() {
    return '<div class="avalna-recommendations__peek" aria-hidden="true">' +
      '<div class="avalna-recommendations__peek-placeholder"></div>' +
      '</div>';
  }

  // --- Recently Viewed: read from localStorage ---
  function avalnaGetRecentlyViewed() {
    try {
      var data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (e) {
      return [];
    }
  }

  // --- Recently Viewed: save to localStorage ---
  function avalnaSaveRecentlyViewed(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {
      // localStorage full or disabled — fail silently
    }
  }

  /* Recently Viewed: drop the orphaned v1 payload.
     The key bump alone already makes v1 unreadable, so this is housekeeping rather than
     part of the fix — it reclaims the quota those entries would otherwise hold forever. */
  function avalnaPurgeLegacyRecentlyViewed() {
    try {
      localStorage.removeItem(STORAGE_KEY_V1);
    } catch (e) {
      // localStorage disabled — nothing to reclaim
    }
  }

  // --- Recently Viewed: record current product ---
  function avalnaRecordCurrentProduct(productData) {
    var items = avalnaGetRecentlyViewed();
    // Remove if already exists (will re-add at front)
    items = items.filter(function(item) { return item.id !== productData.id; });
    // Prepend current product
    items.unshift(productData);
    // Trim to max
    if (items.length > MAX_STORED) {
      items = items.slice(0, MAX_STORED);
    }
    avalnaSaveRecentlyViewed(items);
  }

  // --- Arrow nav: wire up prev/next buttons for a section (Batch 20, desktop only) ---
  function avalnaSetupArrows(section) {
    if (!section || section.avalnaArrowsReady) return;
    var scrollRow = section.querySelector('.avalna-recommendations__scroll-row');
    var prevBtn = section.querySelector('.avalna-recommendations__arrow--prev');
    var nextBtn = section.querySelector('.avalna-recommendations__arrow--next');
    if (!scrollRow || !prevBtn || !nextBtn) return;
    section.avalnaArrowsReady = true;

    function avalnaUpdateArrowState() {
      var maxScroll = scrollRow.scrollWidth - scrollRow.clientWidth;
      prevBtn.disabled = scrollRow.scrollLeft <= 4;
      nextBtn.disabled = maxScroll <= 4 || scrollRow.scrollLeft >= maxScroll - 4;
    }

    function avalnaScrollByCards(direction) {
      var card = scrollRow.querySelector('.avalna-recommendations__card');
      var cardWidth = card ? card.offsetWidth + 12 : 172;
      scrollRow.scrollBy({ left: cardWidth * 3 * direction, behavior: 'smooth' });
    }

    prevBtn.addEventListener('click', function() { avalnaScrollByCards(-1); });
    nextBtn.addEventListener('click', function() { avalnaScrollByCards(1); });
    scrollRow.addEventListener('scroll', avalnaUpdateArrowState);
    window.addEventListener('resize', avalnaUpdateArrowState);
    avalnaUpdateArrowState();
  }

  // --- Hide peek card if scroll row doesn't overflow ---
  function avalnaHidePeekIfNoOverflow(scrollRow) {
    if (!scrollRow) return;
    var peek = scrollRow.querySelector('.avalna-recommendations__peek');
    if (!peek) return;
    // Check if content overflows (needs scrolling)
    if (scrollRow.scrollWidth <= scrollRow.clientWidth + 10) {
      peek.style.display = 'none';
    }
  }

  // --- Show/hide divider based on both sections having content ---
  function avalnaUpdateDivider() {
    var divider = document.querySelector('.avalna-recommendations__divider');
    var recentlyViewed = document.querySelector('.avalna-recently-viewed');
    var relatedProducts = document.querySelector('.avalna-related-products');
    if (!divider) return;

    var rvVisible = recentlyViewed && recentlyViewed.style.display !== 'none';
    var rpVisible = relatedProducts && relatedProducts.style.display !== 'none';

    divider.style.display = (rvVisible && rpVisible) ? '' : 'none';
  }

  /* Recently Viewed: remove a card whose product no longer exists on the storefront.
     v1 rendered these as dead links forever, because nothing ever revalidated the cache. */
  function avalnaDropRecentlyViewedCard(card, productId, scrollRow, section) {
    avalnaSaveRecentlyViewed(avalnaGetRecentlyViewed().filter(function(item) {
      return item.id !== productId;
    }));
    if (card.parentNode) card.parentNode.removeChild(card);

    if (!scrollRow.querySelector('.avalna-recommendations__card')) {
      section.style.display = 'none';
      avalnaUpdateDivider();
    } else {
      avalnaHidePeekIfNoOverflow(scrollRow);
    }
  }

  /* Recently Viewed: fetch each card's CURRENT price and fill the reserved slot.
     Prices are deliberately never persisted (see STORAGE_KEY comment). Each request is
     ~1.4KB gzipped, CDN-cached and HTTP/2-multiplexed, fired after DOMContentLoaded, for
     at most MAX_STORED - 1 cards.
     A 404 means the product was deleted or unpublished — drop the card and forget the
     entry. Any other failure is treated as transient: the card keeps its reserved blank
     price line rather than showing a number we cannot vouch for. */
  function avalnaHydrateRecentlyViewedPrices(section, scrollRow, items) {
    var cards = scrollRow.querySelectorAll('.avalna-recommendations__card');

    items.forEach(function(item, index) {
      var card = cards[index];
      if (!card || !item.handle) return;

      fetch('/products/' + encodeURIComponent(item.handle) + '.js')
        .then(function(response) {
          if (response.status === 404) return null;
          if (!response.ok) throw new Error('avalna-rv-price');
          return response.json();
        })
        .then(function(data) {
          if (data === null) {
            avalnaDropRecentlyViewedCard(card, item.id, scrollRow, section);
            return;
          }
          var slot = card.querySelector('.avalna-recommendations__card-prices');
          if (slot) slot.innerHTML = avalnaBuildPriceHtml(data.price, data.compare_at_price);
        })
        .catch(function() {
          // Transient failure — leave the reserved blank price line in place
        });
    });
  }

  // --- Recently Viewed: render section ---
  function avalnaRenderRecentlyViewed(currentProductId) {
    var items = avalnaGetRecentlyViewed();
    // Exclude current product
    items = items.filter(function(item) { return item.id !== currentProductId; });

    if (items.length === 0) return false;

    var section = document.querySelector('.avalna-recently-viewed');
    var scrollRow = section ? section.querySelector('.avalna-recommendations__scroll-row') : null;
    if (!section || !scrollRow) return false;

    var html = '';
    items.forEach(function(item) {
      html += avalnaBuildCard(item, true);
    });
    html += avalnaBuildPeek();

    scrollRow.innerHTML = html;
    section.style.display = '';
    avalnaHidePeekIfNoOverflow(scrollRow);
    avalnaSetupArrows(section);
    avalnaHydrateRecentlyViewedPrices(section, scrollRow, items);
    return true;
  }

  // --- Related Products: fetch and render ---
  function avalnaFetchRelatedProducts(productId) {
    var url = '/recommendations/products.json?product_id=' + productId + '&limit=' + RECS_LIMIT;

    fetch(url)
      .then(function(response) { return response.json(); })
      .then(function(data) {
        if (!data.products || data.products.length === 0) return;

        var section = document.querySelector('.avalna-related-products');
        var scrollRow = section ? section.querySelector('.avalna-recommendations__scroll-row') : null;
        if (!section || !scrollRow) return;

        var html = '';
        data.products.forEach(function(product) {
          html += avalnaBuildCard({
            id: product.id,
            handle: product.handle,
            title: product.title,
            url: product.url,
            image: product.featured_image || (product.images && product.images[0]) || '',
            price: product.price,
            compareAtPrice: product.compare_at_price
          });
        });
        html += avalnaBuildPeek();

        scrollRow.innerHTML = html;
        section.style.display = '';
        avalnaHidePeekIfNoOverflow(scrollRow);
        avalnaSetupArrows(section);

        // Show divider if recently viewed is also visible
        avalnaUpdateDivider();
      })
      .catch(function() {
        // API failed — silently hide section (section remains display:none)
      });
  }

  // --- Init on DOMContentLoaded ---
  document.addEventListener('DOMContentLoaded', function() {
    var container = document.querySelector('.avalna-recommendations');
    if (!container) return;

    avalnaPurgeLegacyRecentlyViewed();

    var productId = parseInt(container.getAttribute('data-avalna-product-id'), 10);
    var productJsonRaw = container.getAttribute('data-avalna-product-json');
    if (!productId || !productJsonRaw) return;

    // Parse current product data for recording
    try {
      var productFull = JSON.parse(productJsonRaw);
      /* Identity and display fields only — NO price, NO compare_at_price (Batch 31).
         A price written here would be read back on a visit that could be weeks later,
         which is exactly how the phantom discount pill survived the compare-at clear. */
      var currentProduct = {
        id: productFull.id,
        handle: productFull.handle,
        title: productFull.title,
        url: container.getAttribute('data-avalna-product-url'),
        image: productFull.featured_image
          ? (typeof productFull.featured_image === 'string'
              ? productFull.featured_image
              : (productFull.featured_image.src || ''))
          : ''
      };

      // Record this product in recently viewed (before rendering, so current visit is saved)
      avalnaRecordCurrentProduct(currentProduct);

      // Render recently viewed (excluding current product)
      var rvShown = avalnaRenderRecentlyViewed(productId);

      // Update divider state after rendering recently viewed
      // (related products section is still hidden at this point, so divider stays hidden)
      avalnaUpdateDivider();
    } catch (e) {
      // JSON parse failed — skip recently viewed
    }

    // Fetch and render related products (async, non-blocking)
    avalnaFetchRelatedProducts(productId);
  });

})();
